import React from 'react';
import PropTypes from 'prop-types';
import { formatCurrency } from '../../../../utils/formatters';
import { CARTA_DE_CREDITO_TOOLTIP } from '../../../../utils/constantes';
import styles from './SignalTable.module.css';
import NumericField from '../../../form/NumericField/NumericField';
import InfoTooltip from '../../../shared/InfoTooltip/InfoTooltip';
import { useSignalTableLogic } from './SignalTableLogic';

const SignalTable = ({ onSignalsChange, initialSignals = [], pagadoria = false, entradaFacilitada = false, minSignalValue = null, cartaDeCredito = false, atoComplementar = 0 }) => {
  const {
    signals,
    addSignal,
    removeSignal,
    updateSignalValue,
    isLimitReached
  } = useSignalTableLogic(onSignalsChange, initialSignals, entradaFacilitada);

  const getSignalLabel = (index, signal) => {
    if (index !== 0) return signal.description;
    if (cartaDeCredito) return 'Carta de Crédito';
    if (pagadoria) return 'Sinal Ato +Comissão';
    return signal.description;
  };

  return (
    <div className={styles.signalTableContainer}>
      <div className={styles.tableResponsive}>
        <table className={styles.table} id="tabelaSinais">
          <thead>
            <tr>
              <th>Descrição</th>
              <th>Valor (R$)</th>
              <th className={styles.actionsHeader}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {signals.length > 0 ? (
              signals.map((signal, index) => (
                <React.Fragment key={signal.id}>
                  <tr>
                    <td className={styles.descriptionCell}>
                      <span>{getSignalLabel(index, signal)}</span>
                      {index === 0 && (cartaDeCredito || minSignalValue != null) && (
                        // Com Carta de Crédito ativa, mesmo tooltip do toggle (ver getSignalLabel).
                        <InfoTooltip
                          className={styles.inlineHint}
                          text={cartaDeCredito ? CARTA_DE_CREDITO_TOOLTIP : `Valor mínimo de ato: ${formatCurrency(minSignalValue)}`}
                        />
                      )}
                    </td>
                    <td className={styles.valueCell}>
                      <NumericField
                        id={`valor_sinal_${signal.id}`}
                        value={signal.value}
                        onChange={(e) => updateSignalValue(signal.id, e.target.value)}
                        currency={true}
                      />
                    </td>
                    <td className={styles.actionsCell}>
                      <div className={styles.actionButtons}>
                        {index === signals.length - 1 && !isLimitReached && (
                          <button
                            id="adicionarSinal"
                            className={styles.addSignalBtn}
                            onClick={() => addSignal(0)}
                            title="Adicionar Parcela de Sinal"
                            aria-label="Adicionar Parcela de Sinal"
                            type="button"
                          >
                            <i className="fas fa-plus"></i>
                          </button>
                        )}
                        <button
                          className={styles.deleteBtn}
                          title="Remover"
                          onClick={() => removeSignal(signal.id)}
                          type="button"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                  {index === 0 && cartaDeCredito && atoComplementar > 0 && (
                    <tr>
                      <td className={styles.descriptionCell}>
                        <span>Ato Complementar (Carta de Crédito)</span>
                        <InfoTooltip
                          className={styles.inlineHint}
                          text="Quando a Carta de Crédito não cobre o ato mínimo do plano, a própria simulação calcula e devolve este Ato Complementar automaticamente. Não é editável e não é reenviado como um Sinal — a cada nova simulação, a API recalcula o valor."
                        />
                      </td>
                      <td className={styles.valueCell}>
                        <NumericField
                          id="atoComplementar"
                          value={atoComplementar}
                          onChange={() => {}}
                          currency={true}
                          disabled={true}
                        />
                      </td>
                      <td className={styles.actionsCell}></td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan="2" className={styles.emptyMessage}>
                  Nenhum sinal adicionado
                </td>
                <td className={styles.actionsCell}>
                  <div className={styles.actionButtons}>
                    <button
                      id="adicionarSinal"
                      className={styles.addSignalBtn}
                      onClick={() => addSignal(0)}
                      title="Adicionar Parcela de Sinal"
                      aria-label="Adicionar Parcela de Sinal"
                      type="button"
                    >
                      <i className="fas fa-plus"></i>
                    </button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

SignalTable.propTypes = {
  onSignalsChange: PropTypes.func.isRequired,
  initialSignals: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    description: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  })),
  pagadoria: PropTypes.bool,
  entradaFacilitada: PropTypes.bool,
  minSignalValue: PropTypes.number,
  cartaDeCredito: PropTypes.bool,
  atoComplementar: PropTypes.number
};

SignalTable.defaultProps = {
  initialSignals: [],
  pagadoria: false,
  entradaFacilitada: false,
  minSignalValue: null,
  cartaDeCredito: false,
  atoComplementar: 0
};

export default SignalTable;